import { Box } from "@mui/material";
import React, { useMemo } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { packMealGrid } from "../../helpers/packMealGrid";
import { formatMealTime } from "../../helpers/planDates";
import {
  calculateAvailableMealTypes,
  findMealByType,
  isMealLogged,
} from "../../helpers/planSelectors";
import {
  NutritionDailyType,
  PlanMealRootItemType,
} from "../../model/nutritionPlanTypes";
import LoggedBadge from "../shared/LoggedBadge";
import MealItemCard from "./MealItemCard";

type Props = {
  day: NutritionDailyType;
  onOpenItem: (mealType: number, item: PlanMealRootItemType) => void;
};

const COLUMNS = 3;

/**
 * Every meal of a day, in time order, packed into a three-column grid: a
 * meal's cards follow the previous meal's on the same row when there is room
 * and wrap otherwise, so a day of one- and two-item meals fills its rows. A
 * header line (name, time, Logged badge) sits above each run of a meal's
 * cards and spans exactly those columns; a meal that wraps repeats its
 * header on the next row, and a meal that starts mid-row is parted from the
 * previous one by a vertical rule. A Week row shows this when expanded.
 */
const DayMealSections = ({ day, onOpenItem }: Props) => {
  const theme: Theme = useTheme();

  const segments = useMemo(() => {
    const meals = calculateAvailableMealTypes(day.meals).map((type) => {
      const meal = findMealByType(day, type.type);
      return {
        type: type.type,
        label: type.label,
        time: formatMealTime(meal?.time_of_consumption),
        isLogged: isMealLogged(meal),
        items: meal?.items ?? [],
      };
    });
    return packMealGrid(meals, COLUMNS);
  }, [day]);

  if (segments.length === 0) {
    return <Box sx={styles.empty(theme)}>No meals planned for this day.</Box>;
  }

  // Grid rows alternate: headers on the odd lines, cards on the even ones.
  const headerLine = (row: number) => row * 2 + 1;
  const cardLine = (row: number) => row * 2 + 2;

  return (
    <Box sx={styles.grid}>
      {segments.map((segment) => (
        <React.Fragment key={`${segment.meal.type}-${segment.row}`}>
          <Box
            sx={styles.header(theme, segment.row > 0, segment.column > 0)}
            style={{
              gridRow: headerLine(segment.row),
              gridColumn: `${segment.column + 1} / span ${segment.span}`,
            }}
          >
            <Box sx={styles.headerTitle(theme)}>{segment.meal.label}</Box>
            {segment.meal.time && (
              <Box sx={styles.headerTime(theme)}>{segment.meal.time}</Box>
            )}
            {segment.meal.isLogged && <LoggedBadge />}
          </Box>
          {segment.items.length === 0 ? (
            <Box
              sx={{
                ...styles.empty(theme),
                ...styles.divided(theme, segment.column > 0),
              }}
              style={{
                gridRow: cardLine(segment.row),
                gridColumn: segment.column + 1,
              }}
            >
              No items for this meal.
            </Box>
          ) : (
            segment.items.map((rootItem, index) => (
              <Box
                key={rootItem.id}
                sx={{
                  ...styles.cell,
                  // The rule sits beside the meal's first card only.
                  ...styles.divided(theme, segment.column > 0 && index === 0),
                }}
                style={{
                  gridRow: cardLine(segment.row),
                  gridColumn: segment.column + 1 + index,
                }}
              >
                <MealItemCard
                  rootItem={rootItem}
                  onOpen={(item) => onOpenItem(segment.meal.type, item)}
                />
              </Box>
            ))
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default DayMealSections;

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`,
    columnGap: "16px",
    rowGap: "8px",
    // Cards in a row share the tallest one's height, so their borders line up.
    alignItems: "stretch",
    minWidth: 0,
  },
  // Extra room above every header row but the first, so a new row of meals
  // reads as separate from the cards above it. A meal that starts mid-row
  // gets a vertical rule on its left, running from its header down through
  // the 8px row gap to the bottom of its first card (see `divided`).
  header: (theme: Theme, spaced: boolean, divided: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    paddingTop: spaced ? "16px" : 0,
    ...styles.divided(theme, divided),
    ...(divided && { paddingBottom: "8px", marginBottom: "-8px" }),
  }),
  // The rule is drawn in the middle of the 16px column gap: pull the element
  // 8px into the gap and pad the content back.
  divided: (theme: Theme, divided: boolean) =>
    divided
      ? {
          borderLeft: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
          marginLeft: "-8px",
          paddingLeft: "8px",
        }
      : {},
  headerTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
  }),
  headerTime: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "24px",
  }),
  // The slot is a grid so the card stretches to the row's height.
  cell: {
    display: "grid",
    minWidth: 0,
  },
  empty: (theme: Theme) => ({
    padding: "16px 0",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
  }),
};
